import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "@/services/adminService";
import { sellerService } from "@/services/sellerService";
import { walletService } from "@/services/walletService";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import {
  AdminUserDetailPage,
  SalesAgentAssignmentsSection,
  salesAgentDetailToAdmin,
  useAdminUserDetailLabels,
  useAdminUserContactLabels,
} from "@/components/admin/users";

export default function AdminSalesAgentDetailPage() {
  const { t } = useTranslation("salesAgent");
  const { salesAgentId } = useParams<{ salesAgentId: string }>();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const detailLabels = useAdminUserDetailLabels({
    roleLabel: t("detail.roleLabel"),
    statRelevant: t("detail.statAssociations"),
    loadDetail: t("detail.loadDetail"),
    loadError: t("detail.loadError"),
  });

  const contactLabels = useAdminUserContactLabels("salesAgent");

  const {
    data: agent,
    isLoading: isLoadingAgent,
    error: agentError,
  } = useQuery({
    queryKey: ["admin", "sales-agents", salesAgentId],
    queryFn: () => adminService.getSalesAgent(salesAgentId!),
    enabled: !!salesAgentId,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      adminService.updateSalesAgent(salesAgentId!, { is_active: isActive }),
    onSuccess: () => {
      toast.success(t("detail.toastStatusUpdated"));
      void queryClient.invalidateQueries({
        queryKey: ["admin", "sales-agents", salesAgentId],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "sales-agents"] });
    },
    onError: (err) => {
      toastApiError(err, "generic", t("detail.toastStatusError"));
    },
  });

  const updateDisplayNameMutation = useMutation({
    mutationFn: (name: string) =>
      sellerService.updateSellerProfile(salesAgentId!, { name }),
    onSuccess: () => {
      toast.success(detailLabels.toastDisplayNameUpdated);
      void queryClient.invalidateQueries({
        queryKey: ["admin", "sales-agents", salesAgentId],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "sales-agents"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err) => {
      toastApiError(err, "generic", detailLabels.toastDisplayNameError);
    },
  });

  const user = useMemo(
    () => (agent ? salesAgentDetailToAdmin(agent) : undefined),
    [agent],
  );

  if (!salesAgentId) {
    return (
      <div>
        <p className="text-surface-600">{t("detail.invalidRoute")}</p>
      </div>
    );
  }

  return (
    <AdminUserDetailPage
      user={user}
      isLoading={isLoadingAgent}
      hasError={!!agentError}
      listPath="/admin/users"
      labels={detailLabels}
      contactLabels={contactLabels}
      canToggleActive={hasPermission(Permission.SALES_AGENTS_MANAGE_ANY)}
      isTogglingActive={toggleActiveMutation.isPending}
      onToggleActive={() => {
        if (user) {
          toggleActiveMutation.mutate(!user.isActive);
        }
      }}
      canEditDisplayName={hasPermission(Permission.USERS_UPDATE_ANY)}
      isUpdatingDisplayName={updateDisplayNameMutation.isPending}
      onUpdateDisplayName={async (name) => {
        await updateDisplayNameMutation.mutateAsync(name);
      }}
      contactActions={
        hasPermission(Permission.SALES_AGENTS_MANAGE_ANY)
          ? {
              onSendPasswordReset: async () => {
                await adminService.sendSalesAgentPasswordResetEmail(
                  salesAgentId,
                );
                void queryClient.invalidateQueries({
                  queryKey: ["admin", "sales-agents", salesAgentId],
                });
              },
            }
          : undefined
      }
      transactions={{
        fetchTransactions: (params) =>
          walletService.getTransactions(salesAgentId, params),
        fetchStats: (params) =>
          walletService.getStats(salesAgentId, params),
      }}
      associationsContent={
        <SalesAgentAssignmentsSection salesAgentId={salesAgentId} />
      }
    />
  );
}
